import { AnimatePresence, motion } from 'motion/react'
import { CSSProperties, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { includes } from '../../utils/array'
import { AstraParagraph } from '../Typography/AstraParagraph/AstraParagraph'
import { astraTwMerge } from '../Utilities/astraTwMerge'
import { FLOATING_MOTION_PROPS, TRANSFORM_ORIGIN_BY_PLACEMENT } from '../Utilities/floatingMotion'

type ElementWithDataset = HTMLElement | SVGElement
type Placement = (typeof placements)[number]

type TooltipData = {
    tooltip: string
    tooltipPlacement?: string
    tooltipShowArrow?: string
    tooltipNoWrapText?: string
    tooltipSkipDelay?: string
}

type TooltipSearchResult = {
    element: ElementWithDataset
    data: TooltipData
}

let timeoutId: NodeJS.Timeout | null = null
const placements = ['top', 'left', 'bottom', 'right'] as const
const gap = 8

export const OPEN_DELAY = 500

/**
 * How long after a tooltip closes the next one still opens without waiting. Scanning a row of icons
 * would otherwise cost the full delay at every element, which makes a toolbar feel unexplorable.
 */
export const SKIP_DELAY_WINDOW = 200

/**
 * How long a hover should wait before its tooltip appears. A hover that lands while the previous
 * tooltip is still fresh skips the delay, unless the tooltip opted out; `closedAt` of 0 means none has
 * been shown yet.
 */
export function openDelayFor(closedAt: number, now: number, skipDelay = true) {
    return skipDelay && now - closedAt <= SKIP_DELAY_WINDOW ? 0 : OPEN_DELAY
}

const clipPathMap: Record<Placement, string> = {
    top: 'polygon(100% 100%, 0 10px, 100% 0)',
    bottom: 'polygon(0 0, 100% calc(100% - 10px), 0 100%)',
    left: 'polygon(100% 0, 10px 100%, 0 0)',
    right: 'polygon(0 100%, calc(100% - 10px) 0, 100% 100%)',
}

function findFirstParentWithTooltip(target: ElementWithDataset | null): TooltipSearchResult | null {
    if (!target) return null
    const wrapper = target.closest('[data-tooltip-provider="hero-tooltip-wrapper"]') as ElementWithDataset | null
    if (!wrapper) return null
    if (!(wrapper as HTMLElement).matches(':hover')) return null

    const firstChild = wrapper.firstElementChild as ElementWithDataset | null
    if (!firstChild) return null

    const tooltip = (wrapper as HTMLElement).dataset.tooltip
    if (!tooltip || tooltip === 'self') return null

    const data: TooltipData = {
        tooltip,
        tooltipPlacement: wrapper.dataset.tooltipPlacement,
        tooltipShowArrow: wrapper.dataset.tooltipShowArrow,
        tooltipNoWrapText: wrapper.dataset.tooltipNoWrapText,
        tooltipSkipDelay: wrapper.dataset.tooltipSkipDelay,
    }

    return { element: firstChild, data }
}

export const AstraTooltipProvider = () => {
    const ref = useRef<HTMLDivElement | null>(null)
    const activeElementRef = useRef<ElementWithDataset | null>(null)
    const isOpenRef = useRef(false)
    const closedAtRef = useRef(0)

    const [text, setText] = useState<string | null>()
    const [position, setPosition] = useState<DOMRect | null>()
    const [placement, setPlacement] = useState<Placement>('top')
    const [showArrow, setShowArrow] = useState(true)
    const [noWrapText, setNoWrapText] = useState(false)

    const [topPosition, setTopPosition] = useState(0)
    const [leftPosition, setLeftPosition] = useState(0)
    const [clipPath, setClipPath] = useState(clipPathMap['top'])
    const [arrowOverride, setArrowOverride] = useState<CSSProperties>({})

    function onMouseOver(e: MouseEvent) {
        const target = e.target
        const isHTMLElement = target instanceof HTMLElement
        const isSVGElement = target instanceof SVGElement
        if (!isHTMLElement && !isSVGElement) return

        const result = findFirstParentWithTooltip(target)
        if (!result) return

        const { element, data } = result
        activeElementRef.current = element

        const box = element.getBoundingClientRect()
        const placementAttr = data.tooltipPlacement
        const showArrowAttr = data.tooltipShowArrow
        const noWrapTextAttr = data.tooltipNoWrapText

        if (timeoutId) clearTimeout(timeoutId)

        timeoutId = setTimeout(
            () => {
                isOpenRef.current = true
                setText(data.tooltip)
                setPlacement(includes(placements, placementAttr) ? placementAttr : 'top')
                setShowArrow(showArrowAttr !== 'false')
                setNoWrapText(noWrapTextAttr === 'true')
                setTimeout(() => setPosition(box), 1)
            },
            openDelayFor(closedAtRef.current, Date.now(), data.tooltipSkipDelay !== 'false')
        )

        function removeTooltip(ev: Event) {
            if (ref.current && ref.current.contains((ev as MouseEvent).relatedTarget as Node | null)) return
            const t = ev.target
            if (!(t instanceof Element)) return
            if (timeoutId) clearTimeout(timeoutId)
            // Only a tooltip that actually appeared opens the skip window, so an aborted hover still waits.
            if (isOpenRef.current) closedAtRef.current = Date.now()
            isOpenRef.current = false
            setText(null)
            setPosition(null)
            setNoWrapText(false)
            setShowArrow(true)
            activeElementRef.current = null
        }

        target.addEventListener('click', e => removeTooltip(e), { once: true })
        target.addEventListener('mouseout', e => removeTooltip(e), { once: true })
    }

    function closeOnScroll() {
        setText(null)
        setPosition(null)
        activeElementRef.current = null
    }

    useEffect(() => {
        document.addEventListener('mouseover', onMouseOver)
        return () => document.removeEventListener('mouseover', onMouseOver)
    }, [])

    useEffect(() => {
        document.addEventListener('scroll', closeOnScroll, true)
        return () => document.removeEventListener('scroll', closeOnScroll, true)
    }, [])

    useEffect(() => {
        if (!text) return
        const observer = new MutationObserver(() => {
            if (activeElementRef.current && !document.body.contains(activeElementRef.current)) {
                setText(null)
                setPosition(null)
                activeElementRef.current = null
            }
        })
        observer.observe(document.body, { childList: true, subtree: true })
        return () => observer.disconnect()
    }, [text])

    useEffect(() => {
        if (!text || !position) return
        const tooltipHeight = ref.current?.clientHeight ?? 0
        const tooltipWidth = ref.current?.clientWidth ?? 0
        const scrollY = window.scrollY
        const scrollX = window.scrollX
        const targetTop = position.top + scrollY
        const targetLeft = position.left + scrollX
        const targetWidth = position.width
        const targetHeight = position.height

        let topPos: number
        let leftPos: number
        let idealPlacement = placement

        switch (placement) {
            case 'left':
                topPos = targetTop + targetHeight / 2 - tooltipHeight / 2
                leftPos = targetLeft - tooltipWidth - gap
                break
            case 'right':
                topPos = targetTop + targetHeight / 2 - tooltipHeight / 2
                leftPos = targetLeft + targetWidth + gap
                break
            case 'bottom':
                topPos = targetTop + targetHeight + gap
                leftPos = targetLeft + targetWidth / 2 - tooltipWidth / 2
                break
            case 'top':
                topPos = targetTop - tooltipHeight - gap
                leftPos = targetLeft + targetWidth / 2 - tooltipWidth / 2
                if (topPos < scrollY) {
                    topPos = targetTop + targetHeight + gap
                    idealPlacement = 'bottom'
                }
                break
        }

        const idealLeft = leftPos
        const idealTop = topPos

        // Clamp to visible viewport considering scroll offsets
        const minLeft = scrollX
        const maxLeft = scrollX + window.innerWidth - tooltipWidth
        const minTop = scrollY
        const maxTop = scrollY + window.innerHeight - tooltipHeight

        leftPos = Math.min(Math.max(minLeft, leftPos), maxLeft)
        topPos = Math.min(Math.max(minTop, topPos), maxTop)

        const arrowOverride: CSSProperties = {}
        if (idealPlacement === 'top' || idealPlacement === 'bottom') {
            if (leftPos !== idealLeft) {
                arrowOverride.left = tooltipWidth / 2 + (idealLeft - leftPos)
            }
        } else {
            if (topPos !== idealTop) {
                arrowOverride.top = tooltipHeight / 2 + (idealTop - topPos)
            }
        }

        setClipPath(clipPathMap[idealPlacement])
        setPlacement(idealPlacement)
        setTopPosition(topPos)
        setLeftPosition(leftPos)
        setArrowOverride(arrowOverride)
    }, [text, position, placement])

    return createPortal(
        <AnimatePresence>
            {position && text && (
                <motion.div
                    ref={ref}
                    data-tooltip="self"
                    {...FLOATING_MOTION_PROPS}
                    className={astraTwMerge(
                        'pointer-events-none absolute z-priority block w-fit max-w-sm overflow-visible whitespace-pre-wrap rounded-md bg-surface-inverted px-2 py-1 text-center text-xs text-white shadow-tooltip',
                        TRANSFORM_ORIGIN_BY_PLACEMENT[placement]
                    )}
                    style={{
                        zIndex: 9999,
                        top: topPosition,
                        left: leftPosition,
                        maxWidth: '20rem',
                    }}
                >
                    <AstraParagraph className={`font-medium text-inverted ${noWrapText ? 'text-nowrap' : ''}`} variant="body-small">
                        {text}
                    </AstraParagraph>

                    {showArrow && (
                        <div
                            className={astraTwMerge(
                                'pointer-events-none absolute h-2.5 w-2.5 rotate-45 rounded-sm bg-surface-inverted',
                                placement === 'top' ? '-bottom-1 left-1/2 -translate-x-1/2' : '',
                                placement === 'bottom' ? '-top-1 left-1/2 -translate-x-1/2' : '',
                                placement === 'left' ? '-right-1 top-1/2 -translate-y-1/2' : '',
                                placement === 'right' ? '-left-1 top-1/2 -translate-y-1/2' : ''
                            )}
                            style={{ ...arrowOverride, clipPath }}
                        />
                    )}
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    )
}
